/**
 * Compass logo component.
 *
 * Single source of truth for the app logo so future logo swaps only
 * need to touch this file (and `client/public/logo.png`). Used by the
 * top header, the login page, and the dashboard chat PDF export.
 *
 * For HTML export contexts that can't import a React component (e.g.
 * the printPdf iframe srcdoc), use {@link logoUrl} to get the same
 * image URL.
 */

export const LOGO_SRC = '/logo.png';

/** Absolute URL to the logo for use in srcdoc / iframes. */
export function logoUrl(): string {
  return `${window.location.origin}${LOGO_SRC}`;
}

export interface LogoProps {
  /** Pixel size; component is square. Defaults to 32. */
  size?: number;
  /** CSS border-radius. Defaults to a quarter of `size`. */
  radius?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function Logo({ size = 32, radius, className, style }: LogoProps) {
  const r = radius ?? Math.round(size / 6);
  return (
    <img
      src={LOGO_SRC}
      alt="Compass"
      width={size}
      height={size}
      className={className}
      // Width/height as both attributes AND inline style — the inline
      // style is what survives `@media print { * { ... } }` resets in
      // hosts that aggressively normalize printed images.
      style={{
        width: size,
        height: size,
        maxWidth: size,
        maxHeight: size,
        objectFit: 'contain',
        borderRadius: r,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}
